import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Save, Loader2, Settings, Globe, Brain, Shuffle,
  Shield, BarChart3, Eye, AlertTriangle, Check
} from 'lucide-react';
import api from '../../utils/api';


const WEIGHT_PRESETS = [
  { name: 'Balanced', local: 0.40, global: 0.30, rotation: 0.30 },
  { name: 'Local-Heavy', local: 0.60, global: 0.20, rotation: 0.20 },
  { name: 'Global-Heavy', local: 0.20, global: 0.50, rotation: 0.30 },
  { name: 'Max Rotation', local: 0.20, global: 0.20, rotation: 0.60 },
];

export default function AdminExposureRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState(null);
  const [exposureStats, setExposureStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const getToken = () => '';
  const authHeaders = () => ({ 'Content-Type': 'application/json' });

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, statsRes] = await Promise.all([
        api.get(`/admin/exposure-rules`),
        api.get(`/admin/exposure-stats`),
      ]);
      if (rulesRes.ok) {
        const d = rulesRes.data;
        setRules(d.rules);
      }
      if (statsRes.ok) {
        const d = statsRes.data;
        setExposureStats(d);
      }
    } catch (err) {
      console.error('[ExposureRules]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRule = (field, value) => {
    setRules(prev => ({ ...prev, [field]: value }));
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/exposure-rules`, rules);
      {
        const d = res.data;
        setRules(d.rules);
        setDirty(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error('[ExposureRules] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    updateRule('local_weight', preset.local);
    updateRule('global_weight', preset.global);
    updateRule('rotation_weight', preset.rotation);
  };

  if (loading || !rules) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="exposure-rules-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  const weightSum = ((rules.local_weight || 0) + (rules.global_weight || 0) + (rules.rotation_weight || 0)).toFixed(2);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-exposure-rules">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#122033] flex items-center gap-2">
                <Settings className="h-6 w-6 text-[#3BA4C5]" /> Exposure Engine Rules
              </h1>
              <p className="text-sm text-slate-500">Configure how sponsored campaigns are distributed</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={!dirty || saving}
            className={`gap-2 ${saved ? 'bg-emerald-600' : 'bg-[#3BA4C5] hover:bg-[#2d8aab]'}`}
            data-testid="save-rules-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>

        {/* Distribution Weights */}
        <Card className="border border-slate-200 mb-5" data-testid="weights-card">
          <CardContent className="p-5">
            <h3 className="text-base font-bold text-[#122033] mb-1 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#3BA4C5]" /> Distribution Weights
            </h3>
            <p className="text-xs text-slate-400 mb-4">How campaigns are selected for homepage slots</p>

            {/* Presets */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {WEIGHT_PRESETS.map(p => (
                <button key={p.name} onClick={() => applyPreset(p)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 hover:border-[#3BA4C5] transition-colors"
                  data-testid={`preset-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {p.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <Globe className="h-3 w-3" /> Local Weight
                </label>
                <Input type="number" step="0.05" min="0" max="1" value={rules.local_weight}
                  onChange={e => updateRule('local_weight', parseFloat(e.target.value) || 0)}
                  data-testid="weight-local" />
                <p className="text-[10px] text-slate-400">Campaigns matching user country</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <BarChart3 className="h-3 w-3" /> Global Weight
                </label>
                <Input type="number" step="0.05" min="0" max="1" value={rules.global_weight}
                  onChange={e => updateRule('global_weight', parseFloat(e.target.value) || 0)}
                  data-testid="weight-global" />
                <p className="text-[10px] text-slate-400">Globally popular campaigns</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <Shuffle className="h-3 w-3" /> Rotation Weight
                </label>
                <Input type="number" step="0.05" min="0" max="1" value={rules.rotation_weight}
                  onChange={e => updateRule('rotation_weight', parseFloat(e.target.value) || 0)}
                  data-testid="weight-rotation" />
                <p className="text-[10px] text-slate-400">Random rotation for diversity</p>
              </div>
            </div>

            {parseFloat(weightSum) !== 1.0 && (
              <div className="mt-3 flex items-center gap-1.5 text-amber-600 text-xs" data-testid="weight-warning">
                <AlertTriangle className="h-3.5 w-3.5" />
                Weights sum to {weightSum} (recommended: 1.00)
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fair Exposure */}
        <Card className="border border-slate-200 mb-5" data-testid="fairness-card">
          <CardContent className="p-5">
            <h3 className="text-base font-bold text-[#122033] mb-1 flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#3BA4C5]" /> Fair Exposure Protection
            </h3>
            <p className="text-xs text-slate-400 mb-4">Prevent any single clinic from dominating exposure</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs text-slate-500">Max Exposure Per Clinic</label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.05" min="0.05" max="1" value={rules.max_exposure_per_clinic}
                    onChange={e => updateRule('max_exposure_per_clinic', parseFloat(e.target.value) || 0.2)}
                    data-testid="max-exposure" />
                  <span className="text-sm text-slate-500 whitespace-nowrap">{Math.round((rules.max_exposure_per_clinic || 0.2) * 100)}%</span>
                </div>
                <p className="text-[10px] text-slate-400">Clinic enters cooldown if exceeded</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-slate-500">Rolling Window (hours)</label>
                <Input type="number" min="1" max="168" value={rules.rolling_window_hours || 24}
                  onChange={e => updateRule('rolling_window_hours', parseInt(e.target.value) || 24)}
                  data-testid="rolling-window" />
                <p className="text-[10px] text-slate-400">Time window for exposure calculation</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feature Toggles */}
        <Card className="border border-slate-200 mb-5" data-testid="toggles-card">
          <CardContent className="p-5">
            <h3 className="text-base font-bold text-[#122033] mb-4 flex items-center gap-2">
              <Brain className="h-4 w-4 text-[#3BA4C5]" /> Targeting Features
            </h3>
            <div className="space-y-4">
              {[
                { key: 'geo_targeting_enabled', label: 'Geo Targeting', desc: 'Match campaigns to user country/language', Icon: Globe },
                { key: 'behaviour_targeting_enabled', label: 'Behaviour Targeting', desc: 'Use recent browsing behavior for matching', Icon: Brain },
                { key: 'global_rotation_enabled', label: 'Global Rotation', desc: 'Add randomness to campaign selection for diversity', Icon: Shuffle },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center">
                      <item.Icon className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#122033]">{item.label}</p>
                      <p className="text-[10px] text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                  <Switch checked={rules[item.key] !== false}
                    onCheckedChange={v => updateRule(item.key, v)}
                    data-testid={`toggle-${item.key}`} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Live Exposure Stats */}
        {exposureStats && (
          <Card className="border border-slate-200" data-testid="exposure-stats-card">
            <CardContent className="p-5">
              <h3 className="text-base font-bold text-[#122033] mb-1 flex items-center gap-2">
                <Eye className="h-4 w-4 text-[#3BA4C5]" /> Live Exposure Stats
                <Badge variant="outline" className="text-[9px]">Last {exposureStats.window_hours}h</Badge>
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                Total impressions: {exposureStats.total_impressions?.toLocaleString() || 0}
              </p>

              {exposureStats.clinic_exposure?.length > 0 ? (
                <div className="space-y-2">
                  {exposureStats.clinic_exposure.map((c, i) => (
                    <div key={c.clinic_id} className="flex items-center gap-3" data-testid={`clinic-exposure-${c.clinic_id}`}>
                      <span className="text-xs font-medium text-[#122033] w-48 truncate">{c.clinic_name || c.clinic_id}</span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${c.in_cooldown ? 'bg-red-400' : 'bg-[#3BA4C5]'}`}
                          style={{ width: `${Math.min(c.exposure_ratio, 100)}%` }} />
                      </div>
                      <span className={`text-xs font-mono w-14 text-right ${c.in_cooldown ? 'text-red-500 font-bold' : 'text-slate-500'}`}>
                        {c.exposure_ratio}%
                      </span>
                      <span className="text-[9px] text-slate-400 w-12">/ {c.max_allowed}%</span>
                      {c.in_cooldown && (
                        <Badge variant="destructive" className="text-[8px]">Cooldown</Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-300 text-center py-4">No exposure data in the current window</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
